import { computed, ref, watch } from 'vue';
import { useLocalStorage, UrlParams } from '@vueuse/core';
import { defineStore } from 'pinia';
import vtkURLExtract from '@kitware/vtk.js/Common/Core/URLExtract';

import { omit, remapKeys } from '@/src/utils';
import { fileToDataSource, uriToDataSource } from '@/src/io/import/dataSource';
import {
  convertSuccessResultToDataSelection,
  importDataSources,
} from '@/src/io/import/importDataSources';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { useDatasetStore } from '@/src/store/datasets';
import { useImageCacheStore } from '@/src/store/image-cache';
import { Chunk } from '@/src/core/streaming/chunk';
import { ChunkStatus } from '@/src/core/streaming/chunkImage';
import DicomChunkImage from '@/src/core/streaming/dicomChunkImage';
import {
  DicomWebInstanceDataLoader,
  DicomWebInstanceMetaLoader,
} from '@/src/core/streaming/dicom/dicomWebInstanceLoaders';
import { Tags } from '@/src/core/dicomTags';
import { isMobileBrowser } from '@/src/utils/mobileVolume';
import { useMessageStore } from '@/src/store/messages';
import { useDicomMetaStore } from '@/src/store/dicom-web/dicom-meta-store';
import {
  searchForStudies,
  fetchSeries,
  fetchInstanceThumbnail,
  retrieveStudyMetadata,
  retrieveSeriesMetadata,
  retrieveVolumeInstanceMetadata,
  orderVolumeInstanceMetadata,
  parseUrl,
} from '@/src/core/dicom-web-api';
import { useViewStore } from '@/src/store/views';
import { populateAuthorizationToken } from '@/src/utils/token';

const DICOM_WEB_URL_PARAM = 'dicomweb';

export type ProgressState = 'Remote' | 'Pending' | 'Error' | 'Done';

export interface VolumeProgress {
  state: ProgressState;
  loaded: number;
  total: number;
  phase?: 'Downloading' | 'Processing';
  unit?: 'bytes' | 'slices';
}

interface Progress {
  [name: string]: VolumeProgress;
}

export const isDownloadable = (progress?: VolumeProgress) =>
  !progress || ['Pending', 'Done'].every((state) => state !== progress.state);

const fetchFunctions = {
  host: searchForStudies,
  studies: retrieveStudyMetadata,
  series: retrieveSeriesMetadata,
};

const levelToFetchKey = {
  studies: 'studyInstanceUID',
  series: 'seriesInstanceUID',
};

const levelToMetaKey = {
  studies: 'StudyInstanceUID',
  series: 'SeriesInstanceUID',
};

type InitialDicomListFetchProgress = 'Idle' | 'Pending' | 'Done';

/**
 * Collect DICOM data from DICOMWeb
 */
export const useDicomWebStore = defineStore('dicom-web', () => {
  const { VITE_DICOM_WEB_NAME, VITE_DICOM_WEB_URL } = import.meta.env;
  // GUI display name
  const hostName = VITE_DICOM_WEB_NAME
    ? ref(VITE_DICOM_WEB_NAME)
    : useLocalStorage<string>('dicomWebHostName', '');

  // URL param overrides env var, which overrides local storage
  const urlParams = vtkURLExtract.extractURLParameters() as UrlParams;
  const dicomWebFromURLParam = urlParams[DICOM_WEB_URL_PARAM] as
    | string
    | undefined;
  const hostConfig = dicomWebFromURLParam ?? VITE_DICOM_WEB_URL;

  // Don't save URL param or env var to local storage. Only save user input.
  const savedHost = useLocalStorage<string | null>('dicomWebHost', ''); // null if cleared by vuetify text input

  const host = ref<string | null>(hostConfig ?? savedHost.value);

  watch(savedHost, () => {
    host.value = savedHost.value;
  });

  // Remove trailing slash and pull study/series IDs from URL
  const parsedURL = computed(() => parseUrl(host.value ?? ''));

  // A direct series link opens as a lightweight rendered stack by default.
  // The complete volume is only built when the user clicks the advanced-view
  // button, or when a caller explicitly opts into the legacy behaviour with
  // `mode=volume`.
  const quick2DMode = computed(
    () => 'series' in parsedURL.value && urlParams.mode !== 'volume'
  );

  const cleanHost = computed(() => parsedURL.value.host);
  const isConfigured = computed(() => !!cleanHost.value);

  const volumePreviewImages = ref<Record<string, string>>({});
  const volumePreviewRequests = new Map<string, Promise<string | null>>();
  const sliceImages = ref<Record<string, string>>({});
  const sliceRequests = new Map<string, Promise<string | null>>();
  const MAX_CACHED_SLICES = 16;
  let renderedCacheGeneration = 0;

  const orderedVolumeInstances = (volumeKey: string) => {
    const dicoms = useDicomMetaStore();
    return (dicoms.volumeInstances[volumeKey] ?? [])
      .map((instanceKey) => dicoms.instanceInfo[instanceKey])
      .filter(Boolean)
      .sort(
        ({ InstanceNumber: a }, { InstanceNumber: b }) => Number(a) - Number(b)
      );
  };

  const fetchVolumeSlice = async (volumeKey: string, sliceIndex: number) => {
    const dicoms = useDicomMetaStore();
    const instances = orderedVolumeInstances(volumeKey);
    const instanceInfo = instances[sliceIndex];
    if (!instanceInfo) return null;

    const cacheKey = instanceInfo.SopInstanceUID;
    const cachedSlice = sliceImages.value[cacheKey];
    if (cachedSlice) return cachedSlice;

    const pendingRequest = sliceRequests.get(cacheKey);
    if (pendingRequest) return pendingRequest;

    const cacheGeneration = renderedCacheGeneration;
    const request = (async () => {
      const volumeInfo = dicoms.volumeInfo[volumeKey];
      const studyInfo = dicoms.studyInfo[dicoms.volumeStudy[volumeKey]];
      if (!volumeInfo || !studyInfo) return null;

      const preview = await fetchInstanceThumbnail(cleanHost.value, {
        studyInstanceUID: studyInfo.StudyInstanceUID,
        seriesInstanceUID: volumeInfo.SeriesInstanceUID,
        sopInstanceUID: instanceInfo.SopInstanceUID,
      });
      if (cacheGeneration !== renderedCacheGeneration) {
        URL.revokeObjectURL(preview);
        return null;
      }
      sliceImages.value[cacheKey] = preview;
      const sliceKeys = Object.keys(sliceImages.value);
      while (sliceKeys.length > MAX_CACHED_SLICES) {
        const oldestKey = sliceKeys.shift()!;
        const oldPreview = sliceImages.value[oldestKey];
        delete sliceImages.value[oldestKey];
        // A series card may still be using this URL as its thumbnail.
        if (!Object.values(volumePreviewImages.value).includes(oldPreview)) {
          URL.revokeObjectURL(oldPreview);
        }
      }
      return preview;
    })().finally(() => {
      sliceRequests.delete(cacheKey);
    });

    sliceRequests.set(cacheKey, request);
    return request;
  };

  const fetchVolumeThumbnail = async (volumeKey: string) => {
    const cachedPreview = volumePreviewImages.value[volumeKey];
    if (cachedPreview) return cachedPreview;

    const pendingRequest = volumePreviewRequests.get(volumeKey);
    if (pendingRequest) return pendingRequest;

    const request = (async () => {
      const instances = orderedVolumeInstances(volumeKey);
      const middleSlice = Math.floor(instances.length / 2);
      const preview = await fetchVolumeSlice(volumeKey, middleSlice);
      if (!preview) return null;
      volumePreviewImages.value = {
        ...volumePreviewImages.value,
        [volumeKey]: preview,
      };
      return preview;
    })().finally(() => {
      volumePreviewRequests.delete(volumeKey);
    });

    volumePreviewRequests.set(volumeKey, request);
    return request;
  };

  const clearVolumePreviews = () => {
    renderedCacheGeneration++;
    const objectUrls = new Set([
      ...Object.values(volumePreviewImages.value),
      ...Object.values(sliceImages.value),
    ]);
    objectUrls.forEach((preview) => {
      URL.revokeObjectURL(preview);
    });
    volumePreviewImages.value = {};
    sliceImages.value = {};
    volumePreviewRequests.clear();
    sliceRequests.clear();
  };

  const fetchPatientMeta = async (patientKey: string) => {
    const dicoms = useDicomMetaStore();
    const series = await Promise.all(
      dicoms.patientStudies[patientKey]
        .map((studyKey) => dicoms.studyInfo[studyKey])
        .map(async (studyMeta) => {
          const seriesMetas = await retrieveStudyMetadata(cleanHost.value, {
            studyInstanceUID: studyMeta.StudyInstanceUID,
          });
          return seriesMetas.map((seriesMeta) => ({
            ...studyMeta,
            ...seriesMeta,
          }));
        })
    );
    series.flat().forEach((instance) => dicoms.importMeta(instance));
  };

  const fetchVolumesMeta = async (volumeKeys: string[]) => {
    const dicoms = useDicomMetaStore();
    const series = await Promise.all(
      volumeKeys.map(async (volumeKey) => {
        const volumeMeta = dicoms.volumeInfo[volumeKey];
        const studyMeta = dicoms.studyInfo[dicoms.volumeStudy[volumeKey]];
        const instanceMetas = await retrieveSeriesMetadata(cleanHost.value, {
          studyInstanceUID: studyMeta.StudyInstanceUID,
          seriesInstanceUID: volumeMeta.SeriesInstanceUID,
        });
        return instanceMetas.map((instanceMeta) => ({
          ...studyMeta,
          ...volumeMeta,
          ...instanceMeta,
        }));
      })
    );
    series.flat().forEach((instance) => dicoms.importMeta(instance));
  };

  const volumes = ref({} as Progress);

  const activeVolumeKey = computed(() => {
    const pendingVolume = Object.entries(volumes.value).find(
      ([, volume]) => volume.state === 'Pending'
    );
    if (pendingVolume) return pendingVolume[0];

    if (
      'series' in parsedURL.value &&
      typeof parsedURL.value.series === 'string'
    )
      return parsedURL.value.series;
    return null;
  });

  const activePreviewImage = computed(() => {
    const volumeKey = activeVolumeKey.value;
    return volumeKey ? (volumePreviewImages.value[volumeKey] ?? null) : null;
  });

  const activeVolumeProgress = computed(() => {
    const volumeKey = activeVolumeKey.value;
    return volumeKey ? (volumes.value[volumeKey] ?? null) : null;
  });

  const downloadVolume = async (volumeKey: string) => {
    const dicoms = useDicomMetaStore();

    if (!isDownloadable(volumes.value[volumeKey])) return;

    volumes.value[volumeKey] = {
      ...volumes.value[volumeKey],
      state: 'Pending',
      loaded: 0,
      total: 0,
      phase: 'Downloading',
      unit: isMobileBrowser() ? 'slices' : 'bytes',
    };

    const { SeriesInstanceUID: seriesInstanceUID } =
      dicoms.volumeInfo[volumeKey];
    const studyKey = dicoms.volumeStudy[volumeKey];
    const { StudyInstanceUID: studyInstanceUID } = dicoms.studyInfo[studyKey];
    const sopInstanceUIDs = orderedVolumeInstances(volumeKey).map(
      ({ SopInstanceUID }) => SopInstanceUID
    );
    const seriesInfo = {
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUIDs,
    };

    const progressCallback = ({ loaded, total }: ProgressEvent) => {
      volumes.value[volumeKey] = {
        ...volumes.value[volumeKey],
        loaded,
        total,
      };
    };

    try {
      if (isMobileBrowser()) {
        const metadata = await retrieveVolumeInstanceMetadata(
          cleanHost.value,
          seriesInfo
        );
        const byUID = new Map(
          metadata.map((item) => [item.sopInstanceUID, item])
        );
        const ordered = sopInstanceUIDs.map((uid) => byUID.get(uid));
        if (!ordered.length || ordered.some((item) => !item)) {
          throw new Error('Incomplete DICOMweb series metadata');
        }

        const instances = orderVolumeInstanceMetadata(
          ordered.map((item) => item!)
        );
        const first = new Map(instances[0].tags);
        const rows = first.get(Tags.Rows);
        const columns = first.get(Tags.Columns);
        const bitsStored = first.get(Tags.BitsStored);
        if (
          !rows ||
          !columns ||
          !bitsStored ||
          instances.some((item) => {
            const tags = new Map(item.tags);
            return (
              tags.get(Tags.SeriesInstanceUID) !== seriesInstanceUID ||
              tags.get(Tags.Rows) !== rows ||
              tags.get(Tags.Columns) !== columns ||
              Number(tags.get(Tags.NumberOfFrames) ?? 1) > 1
            );
          })
        ) {
          throw new Error(
            'This DICOM series cannot be streamed as a single-frame volume'
          );
        }

        const chunks = instances.map(
          ({ sopInstanceUID, tags }) =>
            new Chunk({
              metaLoader: new DicomWebInstanceMetaLoader(tags),
              dataLoader: new DicomWebInstanceDataLoader(cleanHost.value, {
                studyInstanceUID,
                seriesInstanceUID,
                sopInstanceUID,
              }),
            })
        );
        await Promise.all(chunks.map((chunk) => chunk.loadMeta()));
        const imported = await useDICOMStore().importChunks(chunks, volumeKey);
        if (!imported[volumeKey]) {
          throw new Error('Could not initialize the DICOM volume');
        }

        const image = useImageCacheStore().imageById[
          volumeKey
        ] as DicomChunkImage;
        const waitForVolume = new Promise<void>((resolve, reject) => {
          const cleanup = () => {
            image.removeEventListener('chunkLoad', onChunkLoad);
            image.removeEventListener('chunkError', onChunkError);
          };
          const onChunkLoad = () => {
            const loaded = image
              .getChunkStatuses()
              .filter((status) => status === ChunkStatus.Loaded).length;
            volumes.value[volumeKey] = {
              ...volumes.value[volumeKey],
              loaded,
              total: chunks.length,
            };
            if (loaded === chunks.length) {
              cleanup();
              resolve();
            }
          };
          const onChunkError = ({ error }: { error: unknown }) => {
            cleanup();
            reject(error);
          };
          image.addEventListener('chunkLoad', onChunkLoad);
          image.addEventListener('chunkError', onChunkError);
          if (image.getChunkStatuses().includes(ChunkStatus.Errored)) {
            cleanup();
            reject(new Error('A DICOM slice could not be loaded'));
          } else {
            onChunkLoad();
          }
        });
        try {
          await waitForVolume;
        } catch (error) {
          useImageCacheStore().removeImage(volumeKey);
          useDICOMStore().deleteVolume(volumeKey);
          throw error;
        }

        useDatasetStore().addDataSources([
          {
            dataID: volumeKey,
            dataSource: {
              type: 'collection',
              sources: instances.map(({ sopInstanceUID }) =>
                uriToDataSource(
                  `${cleanHost.value}/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}`,
                  `${sopInstanceUID}.dcm`,
                  'application/dicom'
                )
              ),
            },
          },
        ]);
        useViewStore().setDataForAllViews(volumeKey);
        volumes.value[volumeKey] = {
          ...volumes.value[volumeKey],
          state: 'Done',
          phase: undefined,
        };
        return;
      }

      const files = await fetchSeries(
        cleanHost.value,
        seriesInfo,
        progressCallback
      );

      if (!files) {
        throw new Error('Could not fetch series');
      }

      volumes.value[volumeKey] = {
        ...volumes.value[volumeKey],
        phase: 'Processing',
      };

      const [loadResult] = await importDataSources(files.map(fileToDataSource));
      if (!loadResult) {
        throw new Error('Did not receive a load result');
      }
      if (loadResult.type === 'error') {
        throw loadResult.error;
      }

      const selection = convertSuccessResultToDataSelection(loadResult);
      useViewStore().setDataForAllViews(selection);
      volumes.value[volumeKey] = {
        ...volumes.value[volumeKey],
        state: 'Done',
        phase: undefined,
      };
    } catch (error) {
      const messageStore = useMessageStore();
      messageStore.addError('Failed to load DICOM', { error: error as Error });
      volumes.value[volumeKey] = {
        ...volumes.value[volumeKey],
        state: 'Error',
        loaded: 0,
        phase: undefined,
      };
    }
  };

  const fetchDicomsProgress = ref<InitialDicomListFetchProgress>('Idle');
  const fetchError = ref<undefined | unknown>(undefined);
  // DICOMWeb DataBrowser components automatically expands panels if true
  const linkedToStudyOrSeries = ref(false);
  const fetchDicoms = async () => {
    await populateAuthorizationToken();

    fetchDicomsProgress.value = 'Pending';
    fetchError.value = undefined;
    linkedToStudyOrSeries.value = false;

    const dicoms = useDicomMetaStore();
    dicoms.$reset();
    clearVolumePreviews();
    if (!cleanHost.value) return;

    const deepestLevel = Object.keys(
      parsedURL.value
    ).pop() as keyof typeof fetchFunctions; // at least host key guaranteed to exist

    linkedToStudyOrSeries.value =
      deepestLevel === 'studies' || deepestLevel === 'series';

    const fetchFunc = fetchFunctions[deepestLevel];
    const urlIDs = omit(parsedURL.value, 'host');
    const fetchOptions = remapKeys(urlIDs, levelToFetchKey);
    try {
      const fetchedMetas = await fetchFunc(
        cleanHost.value,
        fetchOptions as any
      );

      const urlMetaIDs = remapKeys(urlIDs, levelToMetaKey);

      const fullMeta = fetchedMetas.map((fetchedMeta) => ({
        ...urlMetaIDs,
        ...fetchedMeta,
      }));
      fullMeta.forEach((instance) => dicoms.importMeta(instance));
    } catch (error) {
      fetchError.value = error;
      console.error(error);
    }

    if (deepestLevel === 'series') {
      const seriesID = Object.values(parsedURL.value).pop() as string;
      void fetchVolumeThumbnail(seriesID).catch((error) => {
        console.warn('Unable to load the DICOM preview image.', error);
      });
      if (!quick2DMode.value) void downloadVolume(seriesID);
    }

    fetchDicomsProgress.value = 'Done';
  };

  const message = computed(() => {
    if (fetchError.value)
      return `Error fetching DICOMWeb data: ${fetchError.value}`;

    const dicoms = useDicomMetaStore();
    if (
      fetchDicomsProgress.value === 'Done' &&
      Object.values(dicoms.patientInfo).length === 0
    )
      return 'Found zero dicoms.';

    return '';
  });

  const isLoading = computed(
    () =>
      fetchDicomsProgress.value === 'Pending' ||
      Object.values(volumes.value).some(({ state }) => state === 'Pending')
  );

  // Safe to call in components' setup()
  let lastFetchedHostUrl: string | null = '';
  const fetchDicomsOnce = () => {
    if (lastFetchedHostUrl !== host.value || message.value) {
      lastFetchedHostUrl = host.value;
      fetchDicoms();
    }
  };

  const loadedDicoms = useDICOMStore();
  loadedDicoms.$onAction(({ name, args, after }) => {
    if (name !== 'deleteVolume') return;

    after(() => {
      const [loadedVolumeKey] = args;
      const volumeKey = Object.keys(volumes.value).find((key) =>
        loadedVolumeKey.startsWith(key)
      );
      if (volumeKey)
        volumes.value[volumeKey] = {
          ...volumes.value[volumeKey],
          state: 'Remote',
          loaded: 0,
        };
    });
  });

  return {
    host,
    quick2DMode,
    savedHost,
    isConfigured,
    hostName,
    message,
    isLoading,
    fetchDicomsProgress,
    volumes,
    volumePreviewImages,
    sliceImages,
    activeVolumeKey,
    activePreviewImage,
    activeVolumeProgress,
    linkedToStudyOrSeries,
    fetchDicomsOnce,
    fetchPatientMeta,
    fetchVolumesMeta,
    fetchVolumeThumbnail,
    fetchVolumeSlice,
    clearVolumePreviews,
    downloadVolume,
  };
});
